import React, { memo, useMemo } from 'react';
import { CodeScannerInput, Text } from 'v3/components';
import UploadChecklistMedia from 'v3/components/UploadChecklistMedia';
import { styles } from '../styles';

import {
  isAnswered,
  hasBackAttachments,
  hasLocalFiles,
  isAttachmentRequiredByBackend,
  shouldRenderUpload,
  isDescriptiveSlug,
  isSelectableSlug,
  isMediaSlug,
  getInputValue,
  isVideoVisualizationSlug,
  getQuestionVideoUrl,
  isWatchOnlyVideoQuestion,
  getQuestionTypeSlug,
  isTrainingPublicQuestionRequired,
} from '../Utils/helper';

function countCorrectOptions(question) {
  const options =
    (Array.isArray(question?.originalQuestion?.values) &&
      question.originalQuestion.values) ||
    (Array.isArray(question?.values) && question.values) ||
    (Array.isArray(question?.responses) && question.responses) ||
    [];

  return options.filter(option => option?.isCorrect === true).length;
}

const Card = memo(function Card({
  q,
  idx,
  locked,
  errVisible,
  qErrors,
  touchQuestion,
  onToggleUnique,
  onToggleMultiple,
  onSetDescriptive,
  onSetUpload,
  onRemoveFile,
}) {
  const slug = getQuestionTypeSlug(q);
  const isCodeReader = slug === 'code' || slug === 'code_reader';
  const isVideoVisualization = isVideoVisualizationSlug(slug);
  const isWatchOnlyVideo = isWatchOnlyVideoQuestion(q);
  const questionVideoUrl = getQuestionVideoUrl(q) || null;
  const attachmentRequired = isAttachmentRequiredByBackend(q);
  const required = isTrainingPublicQuestionRequired(q);
  const showUpload = shouldRenderUpload(q);
  const correctOptionsCount = countCorrectOptions(q);
  const hasMultipleCorrectOptions = correctOptionsCount > 1;
  const shouldUseSingleSelectionOnMultiple = correctOptionsCount <= 1;

  const ok = useMemo(() => {
    return (
      isAnswered(q) &&
      (!attachmentRequired || hasBackAttachments(q) || hasLocalFiles(q))
    );
  }, [q, attachmentRequired]);

  const cardBase =
    typeof styles.questionCard === 'function'
      ? styles.questionCard(ok)
      : styles.questionCard || {};

  const cardStyle = {
    ...cardBase,
    border: errVisible ? '1px solid #FCA5A5' : cardBase.border,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
  };

  return (
    <div style={cardStyle}>
      <div style={{ ...styles.qTop, minWidth: 0 }}>
        <div style={styles.qNumber}>
          {typeof idx === 'string' ? idx : idx + 1}
        </div>

        <div style={{ minWidth: 0, width: '100%' }}>
          <div style={styles.qText}>
            {q.question}
            {required ? <span style={{ color: '#DC2626' }}> *</span> : null}
          </div>

          {hasMultipleCorrectOptions && (
            <div
              style={{
                marginTop: 6,
                color: '#DC2626',
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              * Essa pergunta possui mais de uma opção correta.
            </div>
          )}

          <div style={styles.qDivider} />

          {attachmentRequired ? (
            <Text color="gray" style={{ marginTop: 4, display: 'block' }}>
              Responda anexando arquivo(s). (obrigatório)
            </Text>
          ) : isWatchOnlyVideo ? (
            <Text color="gray" style={{ marginTop: 4, display: 'block' }}>
              Assista ao vídeo obrigatório para prosseguir.
            </Text>
          ) : showUpload ? (
            <Text color="gray" style={{ marginTop: 4, display: 'block' }}>
              Você pode anexar arquivo(s) se necessário.
            </Text>
          ) : null}
        </div>
      </div>

      {(slug === 'unique' || slug === 'logic') && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            width: '100%',
            minWidth: 0,
            marginTop: 12,
            flexWrap: 'wrap',
          }}
        >
          {(q.responses || []).map(r => (
            <button
              key={r.id}
              type="button"
              style={{
                ...(styles.boolBtn ? styles.boolBtn(!!r.selected) : {}),
                flex: 1,
                minWidth: 0,
                width: '100%',
                maxWidth: '100%',
                justifyContent: 'center',
                textAlign: 'center',
                paddingLeft: 10,
                paddingRight: 10,
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
              onClick={() => {
                touchQuestion(q.id);
                onToggleUnique(q.id, r.id);
              }}
              disabled={locked}
            >
              {r.description}
            </button>
          ))}
        </div>
      )}

      {slug === 'multiple' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(q.responses || []).map(r => (
            <button
              key={r.id}
              type="button"
              disabled={locked}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: locked ? 'not-allowed' : 'pointer',
                opacity: locked ? 0.85 : 1,
                width: '100%',
                padding: '8px 10px',
                borderRadius: 10,
                border: '1px solid #E5E7EB',
                backgroundColor: r.selected ? '#EFF6FF' : '#FFFFFF',
                textAlign: 'left',
              }}
              onClick={() => {
                if (locked) return;
                touchQuestion(q.id);
                if (shouldUseSingleSelectionOnMultiple) {
                  onToggleUnique(q.id, r.id);
                } else {
                  onToggleMultiple(q.id, r.id);
                }
              }}
            >
              <input
                type="checkbox"
                checked={!!r.selected}
                aria-label={r.description}
                readOnly
                disabled={locked}
              />
              <Text>{r.description}</Text>
            </button>
          ))}
        </div>
      )}

      {isCodeReader && (
        <CodeScannerInput
          label="Código"
          value={q.questionDescriptiveResponse || ''}
          onChange={value => {
            touchQuestion(q.id);
            onSetDescriptive(q.id, getInputValue(value));
          }}
          disabled={locked}
        />
      )}

      {!isCodeReader &&
        !isWatchOnlyVideo &&
        !isVideoVisualization &&
        (isDescriptiveSlug(slug) ||
          (!isSelectableSlug(slug) &&
            !isDescriptiveSlug(slug) &&
            !isMediaSlug(slug))) && (
          <textarea
            value={q.questionDescriptiveResponse || ''}
            onChange={e => {
              touchQuestion(q.id);
              onSetDescriptive(q.id, getInputValue(e));
            }}
            placeholder="Digite sua resposta..."
            style={{ ...styles.textarea, minWidth: 0 }}
            disabled={locked}
          />
        )}

      {(isVideoVisualization || isWatchOnlyVideo) && (
        <div style={{ marginTop: 12 }}>
          {questionVideoUrl ? (
            <video
              controls
              style={{
                width: '100%',
                maxHeight: 360,
                borderRadius: 8,
                border: '1px solid #E5E7EB',
              }}
            >
              <source src={questionVideoUrl} />
              <track kind="captions" />
            </video>
          ) : (
            <Text color="gray">Vídeo não disponível.</Text>
          )}
        </div>
      )}

      {showUpload && (
        <div style={{ marginTop: 12 }}>
          <UploadChecklistMedia
            name="file"
            label="Arquivo máximo de 200MB"
            accept=".jpg,.jpeg,.png,.pdf,.mkv,.mp4,.mov"
            multiple
            disabled={locked}
            files={Array.from(q?.attachment?.files || [])}
            onFilesChange={nextFiles => {
              touchQuestion(q.id);
              onSetUpload(q.id, nextFiles);
            }}
            onRemove={index => {
              touchQuestion(q.id);
              onRemoveFile(q.id, index);
            }}
          />
        </div>
      )}

      {errVisible && (
        <div
          style={{
            marginTop: 10,
            color: '#DC2626',
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          * {qErrors?.[q.id]}
        </div>
      )}
    </div>
  );
});

export default Card;
